<template>
  <div class="product-pool">
    <div class="pool-header">
      <div class="pool-title-row">
        <div class="pool-title">
          <i class="el-icon-s-goods pool-icon" />
          <span>{{ $l('section_pending_goods') || '待分配产品' }}</span>
          <span class="pool-count">{{ tableData.length }}</span>
        </div>
      </div>

      <PageHeader
        :keyword="keyword"
        :groupFields="groupFields"
        :groupableFields="groupableFields"
        :checkedCount="checkedSummary.skuCount"
        :allGroupsExpanded="allGroupsExpanded"
        :hasGroups="hasGroups"
        @batch-assign="batchAssignSelected"
        @query="handleQuery"
        @change-group-fields="changeGroupFields"
        @toggle-all-groups="toggleAllGroups"
      />
    </div>

    <div class="pool-body">
      <div class="pool-table-wrap">
        <PageMain
          ref="main"
          :tableData="tableData"
          :groupFields="groupFields"
          :loading="loading"
          @assign="$emit('assign', $event)"
          @batch-assign="$emit('batch-assign', $event)"
          @group-assign="$emit('group-assign', $event)"
          @checkbox-change="handleCheckboxChange"
          @expand-state-change="allGroupsExpanded = $event"
          @group-state-change="hasGroups = $event"
        />
      </div>
      <batch-summary-footer
        :checkedSummary="checkedSummary"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from './header.vue'
import PageMain from './main.vue'
import BatchSummaryFooter from './batch-summary-footer.vue'
import tableColumns from './table-columns'
import { add, multiply, round } from '@/libs/util.number'

export default {
  components: { PageHeader, PageMain, BatchSummaryFooter },
  props: {
    shipmentCode: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      tableData: [],
      checkedRows: [],
      keyword: '',
      groupFields: [],
      allGroupsExpanded: false,
      hasGroups: false,
    }
  },
  watch: {
    shipmentCode() {
      this.keyword = ''
      this.groupFields = []
      this.allGroupsExpanded = false
      this.hasGroups = false
      this.initData()
    },
  },
  computed: {
    checkedSummary() {
      return this.getRowsSummary(this.checkedRows)
    },
    groupableFields() {
      return tableColumns(this)
        .filter((column) => column.group && column.field)
        .map((column) => ({
          key: column.field,
          label: column.title,
        }))
    },
  },
  mounted() {
    this.initData()
  },
  methods: {
    async initData() {
      if (!this.shipmentCode) {
        this.resetData()
        return
      }

      this.keyword = ''
      await Promise.all([this.getListData(''), this.getStats()])
    },
    async getListData(keyword = this.keyword) {
      if (!this.shipmentCode) {
        this.resetData()
        return
      }

      this.keyword = keyword
      this.loading = true
      const res = await this.$api
        .ecomShipmentGoodsListContainerPoolApi({
          shipmentCode: this.shipmentCode,
          keyword,
        })
        .catch(() => null)
      this.loading = false

      if (!res) {
        this.tableData = []
        return
      }

      this.tableData = res.body?.data || res.body || []
    },
    handleCheckboxChange(rows = []) {
      this.checkedRows = rows
    },
    batchAssignSelected() {
      if (!this.checkedRows.length) return
      this.$emit('batch-assign', this.checkedRows)
    },
    async confirmBatchAssignRows(rows = []) {
      if (!rows.length) return false

      return this.confirmAssignRows(
        rows,
        this.$l('tips_confirm_batch_assign_goods') ||
          '确认批量分配选中的产品吗？'
      )
    },
    async confirmGroupAssignRows(rows = []) {
      if (!rows.length) return false

      return this.confirmAssignRows(
        rows,
        this.$l('tips_confirm_group_assign_goods') || '确认分配该组产品吗？'
      )
    },
    async confirmAssignRows(rows = [], message) {
      return this.$confirm(
        this.renderBatchConfirmMessage(message, this.getRowsSummary(rows)),
        this.$l('tips_confirm_title') || '提示',
        {
          type: 'warning',
        }
      )
        .then(() => true)
        .catch(() => false)
    },
    getRowsSummary(rows = []) {
      return {
        skuCount: rows.length,
        totalCtnQty: round(
          rows.reduce((sum, row) => add(sum, Number(row.remainCtnQty || 0)), 0),
          2
        ),
        totalVolume: round(
          rows.reduce(
            (sum, row) =>
              add(
                sum,
                row.ctnVolume && row.remainCtnQty
                  ? multiply(row.ctnVolume, row.remainCtnQty)
                  : row.totalVolume || 0
              ),
            0
          ),
          2
        ),
      }
    },
    renderBatchConfirmMessage(message, summary) {
      const h = this.$createElement
      const items = [
        {
          label: this.$l('label_sku_count') || 'SKU数量',
          value: summary.skuCount,
        },
        {
          label: this.$l('form_label_totalCtnQty') || '总箱数',
          value: summary.totalCtnQty,
        },
        {
          label: this.$l('label_total_volume') || '总体积',
          value: `${summary.totalVolume} m³`,
        },
      ]

      return h('div', [
        h(
          'div',
          {
            style: {
              marginBottom: '8px',
              color: '#303133',
            },
          },
          message
        ),
        h(
          'div',
          {
            style: {
              padding: '8px 10px',
              background: '#f8fafc',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
            },
          },
          items.map((item) =>
            h(
              'div',
              {
                style: {
                  display: 'flex',
                  justifyContent: 'space-between',
                  lineHeight: '22px',
                  fontSize: '12px',
                },
              },
              [
                h('span', { style: { color: '#606266' } }, item.label),
                h(
                  'strong',
                  { style: { color: '#303133', marginLeft: '12px' } },
                  String(item.value)
                ),
              ]
            )
          )
        ),
      ])
    },
    changeGroupFields(fields = []) {
      this.groupFields = [...fields]
      this.allGroupsExpanded = false
      this.hasGroups = false
    },
    toggleAllGroups() {
      if (!this.$refs.main) return
      this.$refs.main.toggleAllGroups()
    },
    async getStats() {
      if (!this.shipmentCode) {
        this.emitZeroStats()
        return
      }

      const res = await this.$api
        .ecomShipmentGoodsContainerPoolStatsApi({
          shipmentCode: this.shipmentCode,
        })
        .catch(() => null)

      if (!res) return

      const stats = res.body?.data || res.body || {}
      this.$emit('stats-change', {
        totalCtnQty: Number(stats.totalCtnQty || 0),
        remainCtnQty: Number(stats.remainCtnQty || 0),
      })
    },
    handleQuery(keyword = '') {
      this.getListData(keyword)
    },
    async reload() {
      if (!this.shipmentCode) {
        this.resetData()
        return
      }

      await Promise.all([this.getListData(this.keyword), this.getStats()])
    },
    resetData() {
      this.keyword = ''
      this.groupFields = []
      this.checkedRows = []
      this.tableData = []
      this.allGroupsExpanded = false
      this.hasGroups = false
      this.emitZeroStats()
    },
    emitZeroStats() {
      this.$emit('stats-change', {
        totalCtnQty: 0,
        remainCtnQty: 0,
      })
    },
  },
}
</script>

<style scoped>
.product-pool {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.pool-header {
  padding: 12px 14px 10px;
  flex-shrink: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.pool-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.pool-title {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #1e293b;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.pool-icon {
  color: #409eff;
  font-size: 18px;
}

.pool-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  background: #ecf5ff;
  color: #409eff;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
}

.pool-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pool-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
